import React, { useEffect } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import * as Yup from 'yup'

import { UpdateGroupRequestDTO } from '@/core/api/dtos/request/group/update'
import { Group } from '@/core/api/entities/group'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { ErrorMessage } from '@/shared/components/atoms/ErrorMessage'
import { Input } from '@/shared/components/atoms/Input'
import { Label } from '@/shared/components/atoms/Label/Label'
import {
	Dialog,
	DialogContent,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'

interface EditGroupModalProps {
	visible: boolean
	group: Group | null
	onClose: () => void
	onSubmit: (values: UpdateGroupRequestDTO) => void
	isLoading: boolean
}

const EditGroupModal: React.FC<EditGroupModalProps> = ({
	visible,
	group,
	onClose,
	onSubmit,
	isLoading,
}) => {
	const { t } = useTranslation('common')

	// Create validation schema using Yup
	const validationSchema = Yup.object().shape({
		name: Yup.string()
			.required(t('validation.required'))
			.min(3, t('validation.minLength', { min: 3 }))
			.max(255, t('validation.maxLength', { max: 255 })),
		account_id: Yup.number().required(t('validation.required')),
		music_profile_id: Yup.number().nullable(),
	})

	const { control, handleSubmit, reset } = useForm<UpdateGroupRequestDTO>({
		defaultValues: {
			name: group?.name || '',
			account_id: group?.accountId || 0,
			music_profile_id: group?.musicProfileId || null,
		},
		resolver: yupResolver(validationSchema) as any,
	})

	// Reset form when group changes
	useEffect(() => {
		if (group) {
			reset({
				name: group.name,
				account_id: group.accountId,
				music_profile_id: group.musicProfileId,
			})
		}
	}, [group, reset])

	const handleFormSubmit = (data: UpdateGroupRequestDTO) => {
		onSubmit(data)
	}

	const handleClose = () => {
		if (!isLoading) {
			onClose()
		}
	}

	return (
		<Dialog open={visible} onOpenChange={handleClose}>
			<DialogContent
				className="sm:max-w-[560px] p-0 gap-0 overflow-hidden flex flex-col"
				showCloseButton={false}
			>
				{/* Header */}
				<div className="px-6 py-5 border-b">
					<DialogTitle className="text-lg font-medium">
						{t('entities.group.edit.title')}
					</DialogTitle>
				</div>

				{/* Form Content */}
				<form
					onSubmit={handleSubmit(handleFormSubmit)}
					className="flex flex-col flex-1 min-h-0"
				>
					<div className="px-6 py-5 space-y-5 overflow-y-auto flex-1">
						{/* Group Name Input */}
						<div>
							<Label className="mb-1 block text-xs font-medium">
								{t('entities.group.edit.name_label')}
							</Label>
							<Controller
								name="name"
								control={control}
								render={({ field, fieldState: { error } }) => (
									<div>
										<Input
											{...field}
											placeholder={t('entities.group.edit.name_placeholder')}
											disabled={isLoading}
											className="h-[38px] text-sm bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.1)]"
										/>
										{error && <ErrorMessage error={error.message ?? ''} />}
									</div>
								)}
							/>
						</div>
					</div>

					{/* Footer */}
					<div className="px-6 py-5 border-t flex justify-end gap-2">
						<Button
							type="button"
							variant="outline"
							onClick={handleClose}
							disabled={isLoading}
							className="rounded-full px-4 h-8 text-xs bg-secondary border-border hover:bg-muted"
						>
							{t('actions.cancel')}
						</Button>
						<Button
							type="submit"
							disabled={isLoading}
							className="rounded-full px-4 h-8 text-xs bg-white text-black hover:bg-gray-100"
						>
							{isLoading ? t('actions.loading') : t('actions.save')}
						</Button>
					</div>
				</form>
			</DialogContent>
		</Dialog>
	)
}

export default EditGroupModal
